/**
 * Finance session cache
 *
 * Module-level singleton that keeps finance data in memory for the duration
 * of a browser session.  It is populated on the first navigation to the
 * Finance tab and stays populated while the page is open.  On hard-refresh
 * the module is re-evaluated and the cache starts empty again.
 *
 * This means:
 *  - Zero GitHub API calls when the user is not on the Finance tab.
 *  - Instant re-navigation to Finance after the initial load.
 */

import type {
  ExpenseTransaction,
  FinanceSettings,
  InvestmentsData,
  IncomeData,
} from '@trackmind/core';

export interface FinanceCacheEntry {
  transactions: ExpenseTransaction[];
  autoExcludedItems: ExpenseTransaction[];
  settings: FinanceSettings;
  investments: InvestmentsData;
  income: IncomeData;
  loadedAt: number; // Date.now() timestamp
}

// The single in-memory store — lives at module scope outside React.
let _cache: FinanceCacheEntry | null = null;

export function getFinanceCache(): FinanceCacheEntry | null {
  return _cache;
}

export function setFinanceCache(entry: FinanceCacheEntry): void {
  _cache = entry;
}

export function clearFinanceCache(): void {
  _cache = null;
  _settingsJson = null;
}

/**
 * finance-settings.json exactly as last read or written this session.
 *
 * Each upload in a batch used to rewrite it even when nothing in it changed —
 * one commit per file on GitHub, which with the year file's commit was enough
 * to trip GitHub's secondary rate limit on a 19-file import. A write whose
 * content matches this is skipped.
 */
let _settingsJson: string | null = null;

export function lastSettingsJson(): string | null {
  return _settingsJson;
}

export function rememberSettingsJson(json: string | null): void {
  _settingsJson = json;
}

export function isFinanceCacheLoaded(): boolean {
  return _cache !== null;
}

/**
 * Update settings in the cache without re-fetching transactions.
 */
export function updateCachedSettings(settings: FinanceSettings): void {
  if (_cache) {
    _cache = { ..._cache, settings };
  }
}
